import { z } from 'zod';

export const OPERATIONAL_DURATION_UNITS = Object.freeze(['minute', 'hour', 'day', 'week']);
export const OPERATIONAL_RUN_SOURCE_TYPES = Object.freeze(['CrisisEvent', 'Exercise']);
export const OPERATIONAL_RUN_STATUSES = Object.freeze(['active', 'paused', 'completed', 'cancelled']);
export const OPERATIONAL_TASK_STATUSES = Object.freeze(['blocked', 'ready', 'in_progress', 'completed', 'cancelled']);
export const EXECUTION_MODES = Object.freeze(['sequential', 'parallel']);
export const DEFAULT_EXECUTION_MODE = 'parallel';
export const MAX_OPERATIONAL_MEMBERS = 100;
export const MAX_OPERATIONAL_TASKS = 200;
export const MAX_OPERATIONAL_DEPENDENCIES = 50;
export const MAX_OPERATIONAL_DEPENDENCY_KEY_LENGTH = 145;
export const MAX_OPERATIONAL_RUN_ASSIGNMENTS = 50;
export const MAX_OPERATIONAL_RUN_MEMBERS = 500;
export const MAX_OPERATIONAL_RUN_TASKS = 500;
const MAX_CUSTOM_FIELD_KEYS = 100;
const MAX_CUSTOM_FIELD_BYTES = 64 * 1024;

const LOCALIZED_TEXT = z.union([
    z.string().trim().min(1).max(4000).transform((value) => ({ tr: value, en: value })),
    z.record(z.string().max(20), z.string().trim().max(4000)).refine(
        (value) => Object.values(value).some(Boolean),
        { message: 'At least one localized value is required' },
    ).refine((value) => Object.keys(value).length <= 10, { message: 'Too many localized values' }),
]);
export const OperationalCustomFieldsSchema = z.record(
    z.string().trim().min(1).max(120),
    z.unknown(),
).superRefine((value, ctx) => {
    if (Object.keys(value).length > MAX_CUSTOM_FIELD_KEYS) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Too many custom fields' });
    }
    try {
        if (new TextEncoder().encode(JSON.stringify(value)).length > MAX_CUSTOM_FIELD_BYTES) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Custom fields payload is too large' });
        }
    } catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Custom fields must be JSON serializable' });
    }
});

export const OperationalDurationSchema = z.object({
    value: z.coerce.number().int().positive(),
    unit: z.enum(OPERATIONAL_DURATION_UNITS),
});

export function durationToMilliseconds(duration) {
    if (!duration || !Number.isInteger(duration.value) || duration.value <= 0) {
        throw new TypeError('Duration value must be a positive integer');
    }
    const multipliers = {
        minute: 60 * 1000,
        hour: 60 * 60 * 1000,
        day: 24 * 60 * 60 * 1000,
        week: 7 * 24 * 60 * 60 * 1000,
    };
    const multiplier = multipliers[duration.unit];
    if (!multiplier) throw new TypeError(`Unsupported duration unit: ${duration.unit}`);
    return duration.value * multiplier;
}

export function addOperationalDuration(date, duration) {
    const base = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(base.getTime())) throw new TypeError('A valid base date is required');
    return new Date(base.getTime() + durationToMilliseconds(duration));
}

/**
 * Catalog tasks answer "what has to be done"; timing and blocking live on the
 * BCM assignment so the same duty profile can be scheduled differently per host.
 */
const EXECUTION_TASK_KEY = z.string().trim().min(1).max(120).regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/);
const EXECUTION_TASK_BASE_SHAPE = {
    taskKey: EXECUTION_TASK_KEY,
    responsibleMemberKey: z.string().trim().min(1).max(120),
    title: LOCALIZED_TEXT,
    description: LOCALIZED_TEXT.optional(),
    sequence: z.coerce.number().int().min(0).optional().default(0),
    completionCriteria: LOCALIZED_TEXT.optional(),
    customFields: OperationalCustomFieldsSchema.optional(),
};

export const ExecutionTaskCatalogSchema = z.object(EXECUTION_TASK_BASE_SHAPE);

export function normalizeDependencyTaskKeys(value = []) {
    const seen = new Set();
    const keys = [];
    for (const raw of Array.isArray(value) ? value : []) {
        const key = String(raw || '').trim();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        keys.push(key);
        if (keys.length >= MAX_OPERATIONAL_DEPENDENCIES) break;
    }
    return keys;
}

export const ExecutionTaskAssignmentSchema = z.object({
    ...EXECUTION_TASK_BASE_SHAPE,
    expectedDuration: OperationalDurationSchema.optional().nullable(),
    targetCompletion: OperationalDurationSchema.optional().nullable(),
    warningBefore: OperationalDurationSchema.optional().nullable(),
    dependencyTaskKeys: z.array(
        z.string().trim().min(1).max(MAX_OPERATIONAL_DEPENDENCY_KEY_LENGTH),
    ).max(MAX_OPERATIONAL_DEPENDENCIES).optional().default([]).transform(normalizeDependencyTaskKeys),
});

export const ExecutionModeSchema = z.enum(EXECUTION_MODES);

function hasPositiveDuration(duration) {
    return Boolean(duration)
        && Number.isInteger(Number(duration.value))
        && Number(duration.value) > 0
        && OPERATIONAL_DURATION_UNITS.includes(duration.unit);
}

/**
 * Timing is optional while planning but mandatory before a run can start,
 * because `dueAt` is derived from `targetCompletion`.
 */
export function missingExecutionTaskTiming(tasks = []) {
    return tasks
        .filter((task) => !hasPositiveDuration(task?.targetCompletion))
        .map((task) => String(task?.taskKey || ''));
}

export function normalizeExecutionMode(value) {
    return EXECUTION_MODES.includes(value) ? value : DEFAULT_EXECUTION_MODE;
}

function addDefinitionIssue(ctx, path, message) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
}

export function validateExecutionDefinition(members = [], tasks = [], options = {}) {
    const requireResponsibleMembers = options.requireResponsibleMembers !== false;
    const errors = [];
    const memberKeys = new Set();
    for (const [index, member] of members.entries()) {
        const key = String(member?.memberKey || '').trim();
        if (!key) errors.push({ path: ['members', index, 'memberKey'], message: 'memberKey is required' });
        else if (memberKeys.has(key)) errors.push({ path: ['members', index, 'memberKey'], message: `Duplicate memberKey: ${key}` });
        else memberKeys.add(key);
    }

    const taskKeys = new Set();
    for (const [index, task] of tasks.entries()) {
        const key = String(task?.taskKey || '').trim();
        if (taskKeys.has(key)) errors.push({ path: ['executionTasks', index, 'taskKey'], message: `Duplicate taskKey: ${key}` });
        else taskKeys.add(key);
        if (requireResponsibleMembers && !memberKeys.has(String(task?.responsibleMemberKey || ''))) {
            errors.push({
                path: ['executionTasks', index, 'responsibleMemberKey'],
                message: `Unknown responsibleMemberKey: ${task?.responsibleMemberKey || ''}`,
            });
        }
    }

    return { valid: errors.length === 0, errors };
}

export function refineExecutionDefinition(value, ctx) {
    const result = validateExecutionDefinition(value.members, value.executionTasks);
    for (const issue of result.errors) addDefinitionIssue(ctx, issue.path, issue.message);
}

export const StartOperationalRunSchema = z.object({
    customFields: OperationalCustomFieldsSchema.optional(),
});

const OPERATIONAL_OBJECT_ID = z.string().regex(/^[a-f0-9]{24}$/i, 'Invalid object id');

export const OperationalRunIdParamsSchema = z.object({ id: OPERATIONAL_OBJECT_ID });
export const OperationalRunTaskParamsSchema = z.object({
    id: OPERATIONAL_OBJECT_ID,
    taskId: OPERATIONAL_OBJECT_ID,
});
export const OperationalRunTaskEvidenceParamsSchema = OperationalRunTaskParamsSchema.extend({
    evidenceId: OPERATIONAL_OBJECT_ID,
});

export const CompleteOperationalTaskSchema = z.object({
    completionNote: z.string().trim().max(4000).optional(),
    customFields: OperationalCustomFieldsSchema.optional(),
});

export const AppendOperationalRunUpdateSchema = z.object({
    type: z.enum(['general', 'progress', 'blocker', 'decision']).optional().default('general'),
    message: z.string().trim().min(1).max(8000),
    taskId: OPERATIONAL_OBJECT_ID.optional().nullable(),
    customFields: OperationalCustomFieldsSchema.optional(),
});

export const OperationalRunReasonSchema = z.object({
    reason: z.string().trim().min(1).max(4000),
});

export const OverrideOperationalTaskDependenciesSchema = OperationalRunReasonSchema;

export const ReassignOperationalTaskSchema = z.object({
    memberKey: z.string().trim().min(1).max(120),
    assignmentId: OPERATIONAL_OBJECT_ID.optional(),
    reason: z.string().trim().min(1).max(4000),
});

export const CompleteOperationalRunSchema = z.object({
    summary: z.string().trim().max(8000).optional(),
});

export const OperationalRunResponseSchema = z.object({
    _id: z.unknown(),
    code: z.string(),
    sourceType: z.enum(OPERATIONAL_RUN_SOURCE_TYPES),
    source: z.unknown(),
    sourceCode: z.string().optional(),
    sourceTitle: z.string(),
    status: z.enum(OPERATIONAL_RUN_STATUSES),
    partyInstanceId: z.unknown().optional().nullable(),
    startedAt: z.coerce.date(),
    completedAt: z.coerce.date().optional().nullable(),
    cancelledAt: z.coerce.date().optional().nullable(),
    customFields: z.record(z.unknown()).optional(),
}).passthrough();

export const OperationalRunTaskResponseSchema = z.object({
    _id: z.unknown(),
    run: z.unknown(),
    taskKey: z.string(),
    sourceTaskKey: z.string().optional(),
    responsibleMemberKey: z.string(),
    assignedUserId: z.unknown(),
    status: z.enum(OPERATIONAL_TASK_STATUSES),
    dueAt: z.coerce.date(),
    warningAt: z.coerce.date().optional().nullable(),
}).passthrough();

export const OperationalRunTaskEvidenceResponseSchema = z.object({
    _id: z.unknown(),
    run: z.unknown(),
    task: z.unknown(),
    originalFileName: z.string(),
    mimeType: z.string(),
    size: z.number().nonnegative(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    uploaderId: z.unknown(),
    uploaderSnapshot: z.object({
        userId: z.unknown(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
    }),
    uploadedAt: z.coerce.date(),
}).passthrough();

/**
 * Deep-link for operational-run in-app notifications.
 * Task events open My Work detail; lifecycle events open the crisis/exercise record.
 */
export function operationalNotificationPath({
    eventName = '',
    resourceType = '',
    entityId = '',
    sourceType = '',
    sourceId = '',
} = {}) {
    const event = String(eventName || '');
    const type = String(resourceType || '');
    const id = entityId == null ? '' : String(entityId);
    const source = sourceId == null ? '' : String(sourceId);
    const isTask = type === 'OperationalRunTask' || event.startsWith('operationalRun.task.');
    const isLifecycle = type === 'OperationalRun'
        || (event.startsWith('operationalRun.') && !event.startsWith('operationalRun.task.'));
    if (isTask && id) return `/my-work/operationalRunTask/${id}`;
    if (isLifecycle && source) {
        return sourceType === 'Exercise'
            ? `/bcm/exercises/${source}`
            : `/bcm/crisis-event/${source}`;
    }
    if (isLifecycle && id) return `/my-work/operationalRunControl/${id}`;
    return null;
}

export function operationalSourceDetailPath(sourceType, sourceId) {
    if (sourceId == null || sourceId === '') return '';
    return sourceType === 'Exercise'
        ? `/bcm/exercises/${sourceId}`
        : `/bcm/crisis-event/${sourceId}`;
}

export const OperationalRunBoardResponseSchema = z.object({
    run: OperationalRunResponseSchema,
    tasks: z.array(OperationalRunTaskResponseSchema),
    updates: z.array(z.object({
        _id: z.unknown(),
        type: z.enum(['general', 'progress', 'blocker', 'decision']),
        message: z.string(),
        actorUserId: z.unknown(),
        createdAt: z.coerce.date(),
    }).passthrough()),
});
